/**
 * Upload failures the user can simply retry, with the message to show.
 *
 * The gateway upload routes return 429 `rate_limited` (10 uploads per 60s per
 * user) and 411 `length_required` (a body without Content-Length). Our clients
 * always send a length, so a 411 is unexpected: it is logged and shown as a
 * generic retryable failure.
 */

export const RATE_LIMITED_UPLOAD_MESSAGE = 'Too many uploads, try again in a minute';
export const RETRYABLE_UPLOAD_MESSAGE = 'Upload failed. Please try again.';

const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_LENGTH_REQUIRED = 411;

function statusOf(error: unknown): unknown {
  if (!error || typeof error !== 'object') return undefined;
  return (error as { status?: unknown }).status;
}

/** Whether an upload was rejected by the per-user upload rate limit. */
export function isRateLimitedError(error: unknown): boolean {
  return statusOf(error) === HTTP_TOO_MANY_REQUESTS;
}

/**
 * The message for an upload failure that is worth retrying as-is (429, 411),
 * or null for any other error so the caller can use its own message.
 */
export function getRetryableUploadMessage(error: unknown): string | null {
  if (isRateLimitedError(error)) return RATE_LIMITED_UPLOAD_MESSAGE;
  if (statusOf(error) === HTTP_LENGTH_REQUIRED) {
    console.warn('[upload] Gateway rejected an upload without Content-Length (411):', error);
    return RETRYABLE_UPLOAD_MESSAGE;
  }
  return null;
}
